/**
 * Global search results, grouped by type (F6).
 *
 * The term lives in the URL (`?q=`), not in component state alone: the top bar
 * navigates here with a query, a result is a shareable link, and the browser's
 * back button should restore the search a reader ran. The box on this page
 * writes back to the same place, so typing here and arriving from the top bar
 * are the same code path.
 *
 * Results are grouped by type because F6 asks for it and because a flat list
 * mixing a startup, a filename and a person is harder to scan — a reader knows
 * which kind of thing they are after. The matched term is marked in every hit
 * via {@link Highlight}.
 *
 * @see docs/plan/IMPLEMENTATION_PLAN.md task 7.11, PRD F6
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router';

import type { SearchGroup } from '@nexus/contracts';

import { Card, CardBody, CardHead } from '../../components/ui/Card';
import { Empty } from '../../components/ui/Empty';
import { Icon } from '../../components/ui/Icon';
import { Kicker } from '../../components/ui/Kicker';
import { PageHead } from '../../components/ui/PageHead';

import { useSearch } from './api/search.queries';
import { Highlight } from './components/Highlight';
import { SEARCH_ENTITY_META } from './search-entities';

export function SearchPage(): React.JSX.Element {
  const { t } = useTranslation();
  const [params, setParams] = useSearchParams();
  const term = params.get('q') ?? '';

  /* A controlled input so typing is immediate, seeded from the URL and kept in
     step when the URL changes underneath it — a browser Back, or a fresh
     navigation from the top bar. Re-seeded by comparing during render rather
     than in an effect: a `useEffect` calling `setDraft` sets state
     synchronously and cascades a render (React's lint rule flags it), while
     the compare-and-adjust pattern re-renders before anything is painted. */
  const [draft, setDraft] = useState(term);
  const [seededFrom, setSeededFrom] = useState(term);
  if (seededFrom !== term) {
    setSeededFrom(term);
    setDraft(term);
  }

  const { data, isPending, isError, isFetching } = useSearch({ q: term });

  const handleSubmit = (event: React.SyntheticEvent<HTMLFormElement>): void => {
    event.preventDefault();
    const trimmed = draft.trim();
    /* `replace` so a stream of searches does not stack a history entry per
       keystroke-then-Enter; the reader wants Back to leave search, not to
       walk their own typing backwards. */
    setParams(trimmed === '' ? {} : { q: trimmed }, { replace: true });
  };

  const hasTerm = term.trim().length > 0;

  return (
    <>
      <PageHead kicker={t('nav.group.pipeline')} title={t('search.title')} />

      <form onSubmit={handleSubmit} role="search" className="mb-s4">
        <label htmlFor="search-page-input" className="sr-only">
          {t('search.label')}
        </label>
        <div className="relative max-w-[560px]">
          <Icon
            name="search"
            className="text-gray-400 pointer-events-none absolute top-1/2 left-4 -translate-y-1/2"
          />
          <input
            id="search-page-input"
            type="search"
            value={draft}
            onChange={(event) => {
              setDraft(event.target.value);
            }}
            placeholder={t('search.placeholder')}
            className="border-gray-100 focus:border-blue min-h-12 w-full rounded-full border-[1.5px] bg-white py-3 pr-4 pl-11 text-sm focus:outline-none"
          />
        </div>
      </form>

      <SearchBody
        hasTerm={hasTerm}
        term={term}
        isPending={hasTerm && isPending}
        isFetching={isFetching}
        isError={isError}
        groups={data?.groups ?? []}
        total={data?.total ?? 0}
      />
    </>
  );
}

interface SearchBodyProps {
  hasTerm: boolean;
  term: string;
  isPending: boolean;
  isFetching: boolean;
  isError: boolean;
  groups: readonly SearchGroup[];
  total: number;
}

/** The results area, split out so its states read as one decision. */
function SearchBody({
  hasTerm,
  term,
  isPending,
  isFetching,
  isError,
  groups,
  total,
}: SearchBodyProps): React.JSX.Element {
  const { t } = useTranslation();

  if (!hasTerm) {
    return (
      <Card>
        <Empty title={t('search.promptTitle')}>{t('search.promptBody')}</Empty>
      </Card>
    );
  }

  if (isError) {
    return (
      <p role="alert" className="text-sem-bad">
        {t('errors.generic')}
      </p>
    );
  }

  if (isPending) {
    return (
      <p className="text-body" aria-busy="true">
        {t('search.searching')}
      </p>
    );
  }

  if (total === 0) {
    return (
      <Card>
        <Empty title={t('search.noResultsTitle')}>
          {t('search.noResultsBody', { term })}
        </Empty>
      </Card>
    );
  }

  return (
    <div aria-busy={isFetching} className="flex flex-col gap-s3">
      {groups.map((group) => (
        <SearchGroupCard key={group.entity} group={group} term={term} />
      ))}
    </div>
  );
}

interface SearchGroupCardProps {
  group: SearchGroup;
  term: string;
}

/** One type's hits. */
function SearchGroupCard({ group, term }: SearchGroupCardProps): React.JSX.Element {
  const { t } = useTranslation();
  const meta = SEARCH_ENTITY_META[group.entity];

  return (
    <Card>
      <CardHead>
        <Kicker as="h2" count={group.hits.length}>
          <Icon name={meta.icon} size="sm" />
          {t(`search.group.${meta.labelKey}`)}
        </Kicker>
      </CardHead>
      <CardBody flush>
        <ul>
          {group.hits.map((hit) => {
            const inner = (
              <>
                <span className="text-navy-text text-sm font-medium">
                  <Highlight text={hit.title} query={term} />
                </span>
                {hit.subtitle !== null && (
                  <span className="text-body block text-[13px] font-light">
                    <Highlight text={hit.subtitle} query={term} />
                  </span>
                )}
              </>
            );

            return (
              <li key={hit.id} className="border-gray-100 border-b last:border-b-0">
                {meta.path === null ? (
                  <span className="px-s3 block py-3">{inner}</span>
                ) : (
                  <Link
                    to={meta.path(hit.id)}
                    className="hover:bg-off-white px-s3 block py-3"
                  >
                    {inner}
                  </Link>
                )}
              </li>
            );
          })}
        </ul>
      </CardBody>
    </Card>
  );
}
